// src/components/financials/FundTransferForm.js
// Updated: Tuesday, August 4, 2026 at 8:03 PM EDT

import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Grid,
  MenuItem,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Build, Save, SwapHoriz } from '@mui/icons-material';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useAuth } from '../../context/AuthContext';
import { postFundTransferOrAdjustment } from '../../services/financialPostingService';

const FundTransferForm = ({ onSuccess }) => {
  const { userProfile } = useAuth();
  const [funds, setFunds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mode, setMode] = useState('transfer');

  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    fromFundId: '',
    toFundId: '',
    targetFundId: '',
    adjustmentType: 'increase',
    amount: '',
    memo: '',
  });

  const churchId = userProfile?.churchId;

  const fetchFunds = async () => {
    const snap = await getDocs(
      collection(db, 'churches', churchId, 'financials', 'data', 'funds')
    );

    setFunds(
      snap.docs.map((fundDocument) => ({
        id: fundDocument.id,
        ...fundDocument.data(),
      }))
    );
  };

  useEffect(() => {
    if (churchId) {
      fetchFunds();
    }
  }, [churchId]);

  const handleSubmit = async () => {
    setError('');

    const amount = Number(formData.amount);

    if (!amount || amount <= 0 || !formData.memo.trim()) {
      setError('A positive amount and memo are required.');
      return;
    }

    if (
      mode === 'transfer' &&
      (!formData.fromFundId ||
        !formData.toFundId ||
        formData.fromFundId === formData.toFundId)
    ) {
      setError('Select two different funds.');
      return;
    }

    if (mode === 'adjustment' && !formData.targetFundId) {
      setError('Select a fund to adjust.');
      return;
    }

    setLoading(true);

    try {
      await postFundTransferOrAdjustment(churchId, {
        mode,
        ...formData,
        amount,
      });

      alert('Entry posted and the verified fund balance was updated once.');

      setFormData((previous) => ({
        ...previous,
        amount: '',
        memo: '',
      }));

      await fetchFunds();

      if (onSuccess) {
        onSuccess();
      }
    } catch (submissionError) {
      setError(submissionError.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper
      sx={{
        p: { xs: 2, sm: 3 },
        mb: 3,
        borderLeft: '4px solid',
        borderColor: mode === 'transfer' ? 'primary.main' : 'warning.main',
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
          {mode === 'transfer' ? (
            <SwapHoriz color="primary" sx={{ mr: 1, fontSize: 30 }} />
          ) : (
            <Build color="warning" sx={{ mr: 1, fontSize: 30 }} />
          )}

          <Typography variant="h6">
            {mode === 'transfer' ? 'Internal Transfer' : 'Balance Adjustment'}
          </Typography>
        </Box>

        <ToggleButtonGroup
          value={mode}
          exclusive
          onChange={(event, value) => value && setMode(value)}
          size="small"
          fullWidth
          sx={{
            width: { xs: '100%', sm: 'auto' },
            '& .MuiToggleButton-root': {
              flex: { xs: 1, sm: 'initial' },
              whiteSpace: 'nowrap',
            },
          }}
        >
          <ToggleButton value="transfer">Transfer</ToggleButton>
          <ToggleButton value="adjustment">Correction / Adj</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2} alignItems="center">
        <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={{ minWidth: 0 }}>
          <TextField
            fullWidth
            type="date"
            label="Date"
            InputLabelProps={{ shrink: true }}
            value={formData.date}
            onChange={(event) =>
              setFormData({ ...formData, date: event.target.value })
            }
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={{ minWidth: 0 }}>
          <TextField
            fullWidth
            label="Amount"
            type="number"
            value={formData.amount}
            onChange={(event) =>
              setFormData({ ...formData, amount: event.target.value })
            }
            InputProps={{ startAdornment: '$' }}
          />
        </Grid>

        {mode === 'transfer' ? (
          <>
            <Grid size={{ xs: 12, md: 6 }} sx={{ minWidth: 0 }}>
              <TextField
                select
                fullWidth
                label="From Fund"
                sx={{ width: '100%', minWidth: 0 }}
                value={formData.fromFundId}
                onChange={(event) =>
                  setFormData({
                    ...formData,
                    fromFundId: event.target.value,
                  })
                }
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      sx: { maxWidth: 'calc(100vw - 32px)' },
                    },
                  },
                }}
              >
                {funds.map((fund) => (
                  <MenuItem key={fund.id} value={fund.id}>
                    {fund.name} (${(fund.balance || 0).toLocaleString()})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }} sx={{ minWidth: 0 }}>
              <TextField
                select
                fullWidth
                label="To Fund"
                sx={{ width: '100%', minWidth: 0 }}
                value={formData.toFundId}
                onChange={(event) =>
                  setFormData({
                    ...formData,
                    toFundId: event.target.value,
                  })
                }
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      sx: { maxWidth: 'calc(100vw - 32px)' },
                    },
                  },
                }}
              >
                {funds.map((fund) => (
                  <MenuItem key={fund.id} value={fund.id}>
                    {fund.name} (${(fund.balance || 0).toLocaleString()})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </>
        ) : (
          <>
            <Grid size={{ xs: 12, md: 6 }} sx={{ minWidth: 0 }}>
              <TextField
                select
                fullWidth
                label="Fund to Correct"
                sx={{ width: '100%', minWidth: 0 }}
                value={formData.targetFundId}
                onChange={(event) =>
                  setFormData({
                    ...formData,
                    targetFundId: event.target.value,
                  })
                }
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      sx: { maxWidth: 'calc(100vw - 32px)' },
                    },
                  },
                }}
              >
                {funds.map((fund) => (
                  <MenuItem key={fund.id} value={fund.id}>
                    {fund.name} (${(fund.balance || 0).toLocaleString()})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }} sx={{ minWidth: 0 }}>
              <TextField
                select
                fullWidth
                label="Action"
                sx={{ width: '100%', minWidth: 0 }}
                value={formData.adjustmentType}
                onChange={(event) =>
                  setFormData({
                    ...formData,
                    adjustmentType: event.target.value,
                  })
                }
              >
                <MenuItem value="increase">Increase Balance (+)</MenuItem>
                <MenuItem value="decrease">Decrease Balance (-)</MenuItem>
              </TextField>
            </Grid>
          </>
        )}

        <Grid size={{ xs: 12 }} sx={{ minWidth: 0 }}>
          <TextField
            fullWidth
            label={
              mode === 'transfer'
                ? 'Reason for Transfer'
                : 'Reason for Correction'
            }
            placeholder={
              mode === 'transfer'
                ? 'e.g. Reimbursing General Fund'
                : 'e.g. Correcting Bank Deposit Error'
            }
            value={formData.memo}
            onChange={(event) =>
              setFormData({ ...formData, memo: event.target.value })
            }
          />
        </Grid>

        <Grid
          size={{ xs: 12 }}
          sx={{ minWidth: 0, textAlign: { xs: 'stretch', sm: 'right' } }}
        >
          <Button
            fullWidth
            variant="contained"
            color={mode === 'transfer' ? 'primary' : 'warning'}
            startIcon={<Save />}
            disabled={loading}
            onClick={handleSubmit}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            {mode === 'transfer' ? 'Post Transfer' : 'Post Adjustment'}
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default FundTransferForm;
